import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface AnimatedNavItemProps {
  to: string;
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
  variant?: 'desktop' | 'mobile';
  index?: number;
}

const AnimatedNavItem: React.FC<AnimatedNavItemProps> = ({
  to,
  children,
  isActive = false,
  onClick,
  className = '',
  variant = 'desktop',
  index = 0
}) => {
  const baseClasses = variant === 'desktop' 
    ? `flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 relative ${
        isActive
          ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
          : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-50 dark:hover:bg-gray-800'
      }`
    : `flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset min-h-[48px] shadow-sm ${
        isActive 
          ? 'text-white bg-green-600 font-bold shadow-lg' 
          : 'text-gray-800 hover:text-green-700 hover:bg-green-50 bg-white/60 backdrop-blur-sm border border-gray-200/50'
      }`;

  const mobileItemVariants = {
    hidden: { 
      opacity: 0, 
      x: 50,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        delay: index * 0.1
      }
    },
    hover: {
      scale: 1.02,
      x: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: {
      scale: 0.98,
      x: 2
    }
  };

  const desktopItemVariants = {
    hidden: { 
      opacity: 0, 
      y: -10
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: index * 0.05
      }
    },
    hover: {
      y: -2,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: {
      y: 0,
      scale: 0.98
    }
  };

  const glowVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    hover: { 
      opacity: isActive ? 0.3 : 0.1, 
      scale: 1.1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      className="relative"
      variants={variant === 'mobile' ? mobileItemVariants : desktopItemVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      whileTap="tap"
    >
      {/* Glow effect background */}
      <motion.div
        className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400 to-green-600 opacity-0 blur-sm"
        variants={glowVariants}
        initial="hidden"
        whileHover="hover"
      />
      
      {/* Ripple effect */}
      <motion.div
        className="absolute inset-0 rounded-lg bg-green-500/10"
        initial={{ scale: 0, opacity: 0 }}
        whileTap={{ 
          scale: 1.5, 
          opacity: [0, 0.3, 0],
          transition: { duration: 0.4 }
        }}
      />

      <Link
        to={to}
        onClick={onClick}
        className={`${baseClasses} ${className} relative z-10`}
      >
        <motion.span
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.span>
        
        {/* Active indicator */}
        {isActive && variant === 'mobile' && (
          <motion.div 
            className="w-2 h-2 rounded-full bg-white"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
              delay: 0.2
            }}
          />
        )}
        
        {/* Desktop active underline */}
        {isActive && variant === 'desktop' && (
          <motion.div
            className="absolute bottom-0 left-1/2 w-6 h-0.5 bg-green-500 rounded-full"
            initial={{ scaleX: 0, x: '-50%' }}
            animate={{ scaleX: 1, x: '-50%' }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              delay: 0.1
            }}
          />
        )}
      </Link>
    </motion.div>
  );
};

export default AnimatedNavItem;
